import { useState } from 'react'
import { useAdminProfile, useUpdateAdminProfile, useAdminSettings, useUpdateAdminSettings } from '../lib/admin'
import { useToast } from '../components/ToastContainer'

export default function AdminProfile() {
  const { data: profile, isLoading: profileLoading, error: profileError } = useAdminProfile()
  const updateProfile = useUpdateAdminProfile()
  const { data: settings, isLoading: settingsLoading, error: settingsError } = useAdminSettings()
  const updateSettings = useUpdateAdminSettings()
  const { success: toastSuccess, error: toastError } = useToast()

  const [editName, setEditName] = useState(false)
  const [nameInput, setNameInput] = useState(profile?.full_name ?? '')
  const [editEmail, setEditEmail] = useState(false)
  const [emailInput, setEmailInput] = useState(profile?.email ?? '')
  const [editAdminEmail, setEditAdminEmail] = useState(false)
  const [adminEmailInput, setAdminEmailInput] = useState(settings?.admin_email ?? '')
  const [profileErrorText, setProfileErrorText] = useState('')
  const [settingsErrorText, setSettingsErrorText] = useState('')

  const handleSaveName = async () => {
    setProfileErrorText('')
    try {
      await updateProfile.mutateAsync({ full_name: nameInput })
      setEditName(false)
      toastSuccess('Nome atualizado com sucesso!')
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Erro ao atualizar nome'
      setProfileErrorText(detail)
      toastError(detail)
    }
  }

  const handleSaveEmail = async () => {
    setProfileErrorText('')
    try {
      await updateProfile.mutateAsync({ email: emailInput })
      setEditEmail(false)
      toastSuccess('Email atualizado com sucesso!')
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Erro ao atualizar email'
      setProfileErrorText(detail)
      toastError(detail)
    }
  }

  const handleSaveAdminEmail = async () => {
    setSettingsErrorText('')
    try {
      await updateSettings.mutateAsync({ admin_email: adminEmailInput })
      setEditAdminEmail(false)
      toastSuccess('Email de notificação atualizado!')
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Erro ao atualizar email de notificação'
      setSettingsErrorText(detail)
      toastError(detail)
    }
  }

  const renderField = (
    label: string,
    edit: boolean,
    input: string,
    onEdit: () => void,
    onCancel: () => void,
    onSave: () => void,
    errorText: string,
    inputType?: string,
    setter?: (v: string) => void
  ) => (
    <div className="mb-4">
      <label className="block text-sm font-medium text-[var(--color-text)] mb-1">{label}</label>
      {edit ? (
        <>
          <input
            type={inputType ?? 'text'}
            value={input}
            onChange={(e) => { setter?.(e.target.value) }}
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-subtle)] px-3 py-2 text-sm text-[var(--color-text)] focus:border-[var(--color-brand-400)] focus:ring-1 focus:ring-[var(--color-brand-400)]"
          />
          <div className="mt-2 flex gap-2">
            <button onClick={onSave} className="rounded-lg bg-[var(--color-success)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[var(--color-success)]">
              Salvar
            </button>
            <button onClick={onCancel} className="rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-subtle)]">
              Cancelar
            </button>
          </div>
          {errorText && <p className="mt-1 text-xs text-[var(--color-danger)]">{errorText}</p>}
        </>
      ) : (
        <>
          <p className="text-sm text-[var(--color-text)]">{input || 'Sem nome'}</p>
          <button onClick={onEdit} className="mt-1 text-xs text-[var(--color-brand-500)] hover:underline">
            Editar
          </button>
        </>
      )}
    </div>
  )

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-[var(--color-text)]">Perfil</h2>

      {profileLoading && (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-[var(--color-brand-200)] border-t-[var(--color-brand-500)]" />
        </div>
      )}

      {profileError && (
        <p className="text-[var(--color-danger)]">Erro ao carregar dados da conta</p>
      )}

      {profile && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6 shadow-sm space-y-6">
          <h3 className="text-lg font-semibold text-[var(--color-text)]">Dados da Conta</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {renderField('Nome', editName, nameInput, () => setEditName(true), () => { setEditName(false); setNameInput(profile?.full_name ?? '') }, handleSaveName, profileErrorText, 'text', setNameInput)}
            {renderField('Email', editEmail, emailInput, () => setEditEmail(true), () => { setEditEmail(false); setEmailInput(profile?.email ?? '') }, handleSaveEmail, profileErrorText, 'email', setEmailInput)}

            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1">Status</label>
              <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-[var(--color-success-100)] text-[var(--color-success-700)]">
                {profile.status === 'approved' ? 'Aprovado' : profile.status === 'pending' ? 'Pendente' : 'Rejeitado'}
              </span>
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1">Admin</label>
              <p className="text-sm text-[var(--color-text)]">{profile.is_admin ? 'Sim' : 'Não'}</p>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1">Criado em</label>
              <p className="text-sm text-[var(--color-text-muted)]">{new Date(profile.created_at).toLocaleDateString('pt-BR')}</p>
            </div>
          </div>
        </div>
      )}

      {settingsLoading && (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-[var(--color-brand-200)] border-t-[var(--color-brand-500)]" />
        </div>
      )}

      {settingsError && (
        <p className="text-[var(--color-danger)]">Erro ao carregar configurações</p>
      )}

      {settings && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6 shadow-sm space-y-6">
          <h3 className="text-lg font-semibold text-[var(--color-text)]">Configurações Administrativas</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {renderField('Email de Notificação', editAdminEmail, adminEmailInput, () => setEditAdminEmail(true), () => { setEditAdminEmail(false); setAdminEmailInput(settings?.admin_email ?? '') }, handleSaveAdminEmail, settingsErrorText, 'email', setAdminEmailInput)}

            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1">URL Base da Aplicação</label>
              <p className="text-sm text-[var(--color-text-muted)] break-all">{settings.app_base_url}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
